import { useMemo, useState } from "react";

import { Autocomplete, CircularProgress, TextField } from "@mui/material";

import { useGetDevicesByLocation } from "./useGetDevicesByLocation";

interface IDeviceOption {
  title: string | null | undefined;
  value: string | null | undefined;
  locationId: string | null | undefined;
}

interface IDeviceSelectProps {
  deviceId?: string | null;
  locationId?: string | null;
  showError?: boolean;
  disabled?: boolean;
  onDeviceChange: (value: string) => void;
}

const DeviceSelect = ({
  deviceId,
  locationId,
  showError,
  disabled,
  onDeviceChange,
}: IDeviceSelectProps): JSX.Element => {
  const { data, loading } = useGetDevicesByLocation(locationId);

  const [inputValue, setInputValue] = useState("");

  const memoizedOptions = useMemo((): Array<IDeviceOption> => {
    let options =
      data?.getDevices?.items.map((device): IDeviceOption => {
        return {
          title: device?.name ?? "",
          value: device?.id ?? "",
          locationId: device?.locationId ?? "",
        };
      }) ?? [];

    if (locationId) {
      options = options.filter((option): boolean => {
        return option.locationId === locationId;
      });
    }

    return options;
  }, [data?.getDevices?.items]);

  const memoizedValue = useMemo((): IDeviceOption | null => {
    if (!deviceId) {
      return null;
    }

    return (
      memoizedOptions.find((option): boolean => option?.value === deviceId) ??
      null
    );
  }, [deviceId, memoizedOptions]);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isDisabled = disabled || loading || !locationId;

  return (
    <Autocomplete
      loading={loading}
      value={memoizedValue}
      options={memoizedOptions}
      disabled={isDisabled}
      getOptionLabel={(option): string => option?.title ?? ""}
      onChange={(
        _event: React.SyntheticEvent<Element, Event>,
        newValue: IDeviceOption | null
      ): void => {
        if (newValue) {
          onDeviceChange(newValue?.value ?? "");
        }
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue): void => {
        setInputValue(newInputValue);
      }}
      renderInput={(params): JSX.Element => (
        <TextField
          {...params}
          label="Device"
          error={showError}
          helperText={showError ? "Please select a device" : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default DeviceSelect;
